/* ============================================================
   Bundle Builder — vanilla JS, compact variant
   - Click a product tile -> popover lists size variants
   - Tiered discount per step (defaults: 0%, 20%, 30%)
   - POSTs to /cart/add.js with shared `_bundle_id` line item properties
   ============================================================ */

(function () {
  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.bundleSize = parseInt(root.dataset.bundleSize, 10) || 3;
      this.cartRedirect = root.dataset.cartRedirect === "true";
      this.productHandle = root.dataset.productHandle || "";
      // Section IDs (comma-separated) to re-render after a successful add to cart
      this.refreshSections = (root.dataset.refreshSections || "")
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
      this.cartDrawerSelector = root.dataset.cartDrawerSelector || "";
      this.reloadOnAdd = root.dataset.reloadOnAdd === "true";
      this.fireMeta      = root.dataset.fireMeta      !== "false";
      this.firePinterest = root.dataset.firePinterest !== "false";
      this.fireGa4       = root.dataset.fireGa4       !== "false";
      this.fireTiktok    = root.dataset.fireTiktok    !== "false";

      // Locale-aware money formatter
      // Currency from Shopify (ISO code); locale from the browser.
      const currency = (root.dataset.currency || "USD").trim() || "USD";
      const locale = (typeof navigator !== "undefined" && navigator.language) || "en-US";
      try {
        this.moneyFormatter = new Intl.NumberFormat(locale, {
          style: "currency", currency
        });
      } catch (_) {
        this.moneyFormatter = new Intl.NumberFormat("en-US", {
          style: "currency", currency: "USD"
        });
      }

      // Step-indexed discount % (0% on first, configurable on 2nd/3rd, 0% beyond)
      // NOTE: using getAttribute() rather than `dataset` because attribute names
      // with hyphen-followed-by-digit (e.g. data-discount-tier-2) don't get
      // camelCased by the dataset API — they'd silently read as undefined.
      this.discountTiers = [
        0,
        parseFloat(root.getAttribute("data-discount-tier-2") || "0") / 100,
        parseFloat(root.getAttribute("data-discount-tier-3") || "0") / 100
      ];
      // Pricing mode: "percent" (default — % off the 2nd/3rd case) or
      // "bundle_price" (a fixed total for 2 / 3 cases, e.g. "2 for $55").
      // In bundle_price mode `bundlePrices[i]` is the target TOTAL (cents)
      // once step i is filled; 0 = no offer for that step.
      this.pricingMode = root.dataset.pricingMode === "bundle_price" ? "bundle_price" : "percent";
      this.bundlePrices = [
        0,
        parseInt(root.getAttribute("data-bundle-price-2") || "0", 10) || 0,
        parseInt(root.getAttribute("data-bundle-price-3") || "0", 10) || 0
      ];
      // Toggleable per-step discount indicator (block setting).
      // Default true if the attribute is missing (e.g. older Liquid output).
      this.showStepDiscount = root.dataset.showStepDiscount !== "false";

      // Rewards bar config
      this.shippingThresholdCents = parseInt(root.dataset.shippingThresholdCents || "0", 10);
      this.shippingLabel = root.dataset.shippingLabel || "Free shipping";

      this.ctaEmptyLabel = root.dataset.ctaEmpty   || "Pick at least 1 case";
      this.ctaPartialTpl = root.dataset.ctaPartial || "Add {count} to cart — {total}";
      this.ctaFullTpl    = root.dataset.ctaFull    || "Add bundle — {total}";

      const cfgEl  = root.querySelector("[data-bundle-config]");
      const prodEl = root.querySelector("[data-bundle-products]");
      if (!cfgEl || !prodEl) return; // empty state

      const cfg = JSON.parse(cfgEl.textContent);
      this.stepTitles = cfg.stepTitles || [];

      this.products = JSON.parse(prodEl.textContent.trim());

      // Current product — locks step 1 if we're on a product page
      const curEl = root.querySelector("[data-bundle-current-product]");
      this.currentProduct = null;
      if (curEl) {
        const raw = curEl.textContent.trim();
        if (raw && raw !== "null") {
          try { this.currentProduct = JSON.parse(raw); } catch (_) {}
        }
      }

      this.stepsEl     = root.querySelector("[data-bundle-steps]");
      this.totalEl     = root.querySelector("[data-bundle-total]");
      this.rewardsEl   = root.querySelector("[data-bundle-rewards]");
      this.ctaEl       = root.querySelector("[data-bundle-cta]");
      this.popoverEl   = root.querySelector("[data-bundle-popover]");
      this.popTitleEl  = root.querySelector("[data-bundle-popover-title]");
      this.popOptsEl   = root.querySelector("[data-bundle-popover-options]");
      this.popCloseEl  = root.querySelector("[data-bundle-popover-close]");
      this.shippingEl     = root.querySelector("[data-bundle-shipping]");
      this.shippingTextEl = root.querySelector("[data-bundle-shipping-text]");
      this.savingsEl      = root.querySelector("[data-bundle-savings]");

      this.state = { bundle: [] };
      this.popoverAnchor = null;
      this.popoverInBody = false;
      this.expanded = false; // active gallery step starts collapsed behind "+"

      // 1. Try restoring from localStorage (same product, within 24h)
      this.restoreFromStorage();

      // 2. Pre-fill slot 1 ONLY when the URL carries an explicit ?variant=X
      // (deep-link from a campaign or shared link). Otherwise the variant
      // picker remains the customer's job — the bundle builder is the picker.
      if (this.state.bundle.length === 0 && this.currentProduct) {
        const selectedId = this.currentProduct.selectedVariantId;
        if (selectedId) {
          const v = (this.currentProduct.variants || [])
            .find(x => x.variantId === selectedId && x.available);
          if (v) {
            this.state.bundle.push({
              productId: this.currentProduct.productId,
              variantId: v.variantId
            });
          }
        }
      }

      // 3. URL ?bundle_case=<handle> pre-selects an additional product into the bundle
      this.applyUrlPreselect();

      this.ctaEl.addEventListener("click", () => this.submit());
      if (this.popCloseEl) {
        this.popCloseEl.addEventListener("click", (e) => {
          e.stopPropagation();
          this.hidePopover();
        });
      }
      this.bindPopoverDismiss();
      this.render();
    }

    // ---- helpers ----
    fmt(cents) {
      return this.moneyFormatter.format((cents || 0) / 100);
    }

    escape(s) {
      return String(s == null ? "" : s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    }

    interpolate(tpl, vars) {
      return tpl.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : `{${k}}`));
    }

    count()  { return this.state.bundle.length; }
    full()   { return this.count() === this.bundleSize; }

    productById(id)   {
      const fromGallery = this.products.find(p => p.productId === id);
      if (fromGallery) return fromGallery;
      // Fall back to the locked current product (may not be in eligible collection)
      if (this.currentProduct && this.currentProduct.productId === id) {
        return this.currentProduct;
      }
      return null;
    }
    variantOfPick(pk) {
      const p = this.productById(pk.productId);
      if (!p) return null;
      return p.variants.find(v => v.variantId === pk.variantId) || null;
    }
    pickPrice(pk) { const v = this.variantOfPick(pk); return v ? v.price : 0; }

    discountPct(stepIndex)  { return this.discountTiers[stepIndex] || 0; }
    bundlePrice(stepIndex)  { return this.bundlePrices[stepIndex] || 0; }
    isBundlePriceMode()     { return this.pricingMode === "bundle_price"; }
    // Does step N carry an offer at all? (drives "Optional" tags + rewards bar)
    tierActive(stepIndex) {
      return this.isBundlePriceMode()
        ? this.bundlePrice(stepIndex) > 0
        : this.discountPct(stepIndex) > 0;
    }
    // Discounted total of the first `n` picks (steps 0..n-1)
    totalUpTo(n) {
      let s = 0;
      for (let i = 0; i < Math.min(n, this.state.bundle.length); i++) {
        s += this.discountedPrice(i, this.pickPrice(this.state.bundle[i]));
      }
      return s;
    }
    discountedPrice(stepIndex, basePrice) {
      if (this.isBundlePriceMode()) {
        const target = this.bundlePrice(stepIndex);
        if (target <= 0 || stepIndex === 0) return basePrice;
        // This case costs whatever gets the running total to the bundle price,
        // never less than $0 and never more than its own full price.
        const prev = this.totalUpTo(stepIndex);
        return Math.max(0, Math.min(basePrice, target - prev));
      }
      return Math.round(basePrice * (1 - this.discountPct(stepIndex)));
    }
    rawTotal()        { return this.state.bundle.reduce((s, pk) => s + this.pickPrice(pk), 0); }
    discountedTotal() { return this.totalUpTo(this.state.bundle.length); }
    savings() { return this.rawTotal() - this.discountedTotal(); }

    // ---- render ----
    render() {
      this.renderSteps();
      this.renderFooter();
      this.renderShipping();
    }

    renderShipping() {
      if (!this.shippingEl || !this.shippingTextEl) return;
      // Hide entirely if no threshold is set OR the bundle is still empty —
      // avoids anchoring the customer to "$80 away from free shipping" on a
      // blank bundle before they've engaged at all.
      if (this.shippingThresholdCents <= 0 || this.count() === 0) {
        this.shippingEl.hidden = true;
        return;
      }
      this.shippingEl.hidden = false;
      const total = this.discountedTotal();
      if (total >= this.shippingThresholdCents) {
        this.shippingEl.dataset.state = "unlocked";
        this.shippingTextEl.textContent = `${this.shippingLabel} unlocked`;
      } else {
        this.shippingEl.dataset.state = "locked";
        const remaining = this.shippingThresholdCents - total;
        this.shippingTextEl.textContent =
          `Add ${this.fmt(remaining)} to unlock ${this.shippingLabel.toLowerCase()}`;
      }
    }

    renderSteps() {
      this.stepsEl.innerHTML = "";
      const bundleLen = this.state.bundle.length;

      for (let i = 0; i < this.bundleSize; i++) {
        const isLockedFirst = i === 0 && !!this.currentProduct;
        const hasPick = i < bundleLen;
        // Slot 0 (when locked) is always "active" — its dropdown is permanently
        // editable. For other slots: completed if it has a pick, active if it's
        // the next-to-fill, otherwise future.
        const nextOptionalIdx = bundleLen;
        const state =
          isLockedFirst ? "active" :
          hasPick ? "completed" :
          i === nextOptionalIdx ? "active" : "future";

        const li = document.createElement("li");
        li.className = "bb-step";
        li.dataset.state = state;
        const activeIdx = nextOptionalIdx; // for downstream code that still uses it

        // Header
        const head = document.createElement("div");
        head.className = "bb-step-head";

        // Optional tag — small uppercase prefix on the slot 2+ titles to make
        // it clear those slots are not required.
        if (!isLockedFirst && !hasPick && this.tierActive(i)) {
          const tag = document.createElement("span");
          tag.className = "bb-step-optional-tag";
          tag.textContent = "Optional";
          head.appendChild(tag);
        }

        const title = document.createElement("h3");
        title.className = "bb-step-title";
        title.textContent = this.stepTitles[i] || `pick case ${i + 1}`;
        head.appendChild(title);

        li.appendChild(head);

        // Body
        const body = document.createElement("div");
        body.className = "bb-step-body";
        if (isLockedFirst) {
          // Always show the variant picker for the page's product
          body.appendChild(this.buildLockedFirstStep());
        } else if (hasPick) {
          body.appendChild(this.buildPickSummary(i));
        } else if (i === activeIdx) {
          // active optional slot: gallery or "+" button
          if (this.expanded) {
            body.appendChild(this.buildGalleryFrame());
          } else {
            body.appendChild(this.buildAddButton());
          }
        } else {
          // Future step: dimmed optional preview placeholder
          body.appendChild(this.buildOptionalPreview());
        }
        li.appendChild(body);
        this.stepsEl.appendChild(li);
      }
    }

    buildOptionalPreview() {
      const wrap = document.createElement("div");
      wrap.className = "bb-add-button bb-add-button--optional";
      wrap.setAttribute("aria-disabled", "true");
      wrap.innerHTML =
        `<span class="bb-optional-tag">Optional</span>` +
        `<span class="bb-add-button-plus" aria-hidden="true">+</span>`;
      return wrap;
    }

    buildAddButton() {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bb-add-button";
      btn.setAttribute("aria-label", "Reveal products");
      btn.innerHTML = `<span class="bb-add-button-plus" aria-hidden="true">+</span>`;
      btn.addEventListener("click", () => {
        this.expanded = true;
        this.render();
      });
      return btn;
    }

    buildLockedFirstStep() {
      const p = this.currentProduct;
      const wrap = document.createElement("div");
      wrap.className = "bb-locked";

      // Header: thumb + product name + hint
      const header = document.createElement("div");
      header.className = "bb-locked-header";
      header.appendChild(this.makeThumb(p, "bb-locked-thumb"));
      const meta = document.createElement("div");
      meta.className = "bb-locked-meta";
      meta.innerHTML =
        `<div class="bb-locked-name">${this.escape(p.title)}</div>` +
        `<div class="bb-locked-hint">Select a size</div>`;
      header.appendChild(meta);
      wrap.appendChild(header);

      // Size dropdown — always visible, reflects whatever variant currently
      // occupies slot 0 (if any). Changing the dropdown updates slot 0 in place.
      const slot0 = this.state.bundle[0];
      const currentVariantId =
        (slot0 && slot0.productId === p.productId) ? slot0.variantId : null;

      const select = document.createElement("select");
      select.className = "bb-locked-select";
      select.setAttribute("aria-label", `Pick a size for ${p.title}`);

      // Placeholder shown only when nothing is selected yet
      if (currentVariantId == null) {
        const placeholder = document.createElement("option");
        placeholder.value = "";
        placeholder.textContent = "Select a size";
        placeholder.disabled = true;
        placeholder.selected = true;
        select.appendChild(placeholder);
      }

      p.variants.forEach(v => {
        const opt = document.createElement("option");
        opt.value = String(v.variantId);
        // Use Shopify's option1 directly (the actual size value, free of color
        // or other option dimensions). Falls back to the variant title if a
        // product happens to have no options at all.
        const fullTitle = (v.title && v.title !== "Default Title") ? v.title : p.title;
        const title = v.option1 || fullTitle;
        opt.textContent = v.available ? title : `${title} — Sold out`;
        opt.disabled = !v.available;
        if (currentVariantId != null && currentVariantId === v.variantId) {
          opt.selected = true;
        }
        select.appendChild(opt);
      });

      select.addEventListener("change", (e) => {
        const id = parseInt(e.target.value, 10);
        if (Number.isFinite(id)) this.setLockedVariant(id);
      });

      wrap.appendChild(select);
      return wrap;
    }

    buildPickSummary(stepIndex) {
      const pk = this.state.bundle[stepIndex];
      const p = this.productById(pk.productId);
      const v = this.variantOfPick(pk);
      const base = v ? v.price : 0;
      const final = this.discountedPrice(stepIndex, base);
      const pct = this.discountPct(stepIndex);

      const wrap = document.createElement("div");
      wrap.className = "bb-pick";

      wrap.appendChild(this.makeThumb(p, "bb-pick-thumb"));

      const meta = document.createElement("div");
      meta.className = "bb-pick-meta";
      const variantLabel = v && v.title && v.title !== "Default Title"
        ? ` · ${this.escape(v.title)}` : "";
      let priceHtml = `<span class="bb-pick-final">${this.fmt(final)}</span>`;
      if (this.isBundlePriceMode()) {
        if (final < base) {
          priceHtml +=
            `<span class="bb-pick-orig">${this.fmt(base)}</span>` +
            `<span class="bb-pick-saving">save ${this.fmt(base - final)}</span>`;
        }
      } else if (pct > 0) {
        priceHtml +=
          `<span class="bb-pick-orig">${this.fmt(base)}</span>` +
          `<span class="bb-pick-saving">${Math.round(pct * 100)}% off</span>`;
      }
      meta.innerHTML =
        `<div class="bb-pick-name">${this.escape(p.title)}${variantLabel}</div>` +
        `<div class="bb-pick-price">${priceHtml}</div>`;
      wrap.appendChild(meta);

      const change = document.createElement("button");
      change.type = "button";
      change.className = "bb-pick-change";
      change.textContent = "Change";
      change.title = "Re-pick this case (clears any later picks too)";
      change.addEventListener("click", () => this.changeStep(stepIndex));
      wrap.appendChild(change);

      return wrap;
    }

    buildGalleryFrame() {
      const frame = document.createElement("div");
      frame.className = "bb-frame";

      // Close button — collapses the grid back behind the "+" button
      const close = document.createElement("button");
      close.type = "button";
      close.className = "bb-frame-close";
      close.setAttribute("aria-label", "Close");
      close.innerHTML = "&times;";
      close.addEventListener("click", (e) => {
        e.stopPropagation();
        this.expanded = false;
        this.hidePopover();
        this.render();
      });
      frame.appendChild(close);

      const grid = document.createElement("div");
      grid.className = "bb-grid";

      this.products.forEach(p => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "bb-card";
        btn.title = p.title;
        btn.setAttribute("aria-label", p.title);

        const anyAvail = p.variants.some(v => v.available);
        if (!anyAvail) btn.disabled = true;

        btn.appendChild(this.makeThumb(p, "bb-card-thumb"));

        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          if (btn.disabled) return;
          const availVariants = p.variants.filter(v => v.available);
          if (availVariants.length === 1) {
            this.addToBundle(p.productId, availVariants[0].variantId);
          } else {
            this.openPopover(btn, p);
          }
        });
        grid.appendChild(btn);
      });

      frame.appendChild(grid);
      return frame;
    }

    makeThumb(product, className) {
      if (product && product.image) {
        const img = document.createElement("img");
        img.className = className;
        img.src = product.image;
        img.alt = product.title || "";
        img.loading = "lazy";
        return img;
      }
      const div = document.createElement("div");
      div.className = `${className} bb-card-thumb--placeholder`;
      div.textContent = (product && product.title ? product.title : "?").charAt(0).toUpperCase();
      return div;
    }

    renderFooter() {
      const raw = this.rawTotal();
      const total = this.discountedTotal();
      const saved = this.savings();
      const totalStr = this.fmt(total);

      // "You saved $X" celebration line (shown only when there are real savings)
      if (this.savingsEl) {
        if (saved > 0) {
          this.savingsEl.hidden = false;
          this.savingsEl.innerHTML =
            `<span>You saved</span>` +
            `<span class="bb-savings-amount">${this.escape(this.fmt(saved))}</span>`;
        } else {
          this.savingsEl.hidden = true;
        }
      }

      // Total — discounted prominent, original struck-through when there are savings
      if (saved > 0) {
        this.totalEl.innerHTML =
          `${totalStr} <span class="bb-total-orig">${this.fmt(raw)}</span>`;
      } else {
        this.totalEl.textContent = totalStr;
      }

      // Brief pulse animation when total actually changes
      if (this.lastRenderedTotal !== undefined && this.lastRenderedTotal !== total) {
        this.totalEl.classList.remove("bb-total--pulse");
        // force reflow so the animation restarts
        void this.totalEl.offsetWidth;
        this.totalEl.classList.add("bb-total--pulse");
      }
      this.lastRenderedTotal = total;

      const count = this.count();
      const d2 = Math.round(this.discountPct(1) * 100);
      const d3 = Math.round(this.discountPct(2) * 100);
      const has2 = this.tierActive(1) && this.bundleSize >= 2;
      const has3 = this.tierActive(2) && this.bundleSize >= 3;
      const bundleMode = this.isBundlePriceMode();

      // Once the bundle is complete the "You saved $X" celebration line
      // already communicates the discount — hide the rewards line entirely
      // to avoid saying the same thing twice.
      const bundleComplete = count >= this.bundleSize;
      if ((!has2 && !has3) || bundleComplete) {
        this.rewardsEl.innerHTML = "";
        this.rewardsEl.hidden = true;
      } else {
        this.rewardsEl.hidden = false;
        const sep = `<span class="bb-glyph">✦</span>`;
        const ORDINALS = ["1st", "2nd", "3rd", "4th", "5th", "6th"];

        const calloutFor = (tierIdx, pctValue) => {
          const tierCount = tierIdx + 1;
          const ordinal = ORDINALS[tierIdx] || `${tierCount}th`;
          if (bundleMode) {
            // Fixed bundle price copy: "2 cases for $55"
            const priceStr = this.fmt(this.bundlePrice(tierIdx));
            if (count >= tierCount) {
              const rawUpTo = this.state.bundle.slice(0, tierCount)
                .reduce((s, pk) => s + this.pickPrice(pk), 0);
              const savedUpTo = rawUpTo - this.totalUpTo(tierCount);
              return `<span class="bb-reward-hit">✓ ${tierCount} cases for ${priceStr} (save ${this.fmt(savedUpTo)})</span>`;
            }
            const remaining = tierCount - count;
            if (count === 0) {
              return `<strong>${tierCount} cases for ${priceStr}</strong>`;
            }
            return `Add ${remaining} more for <strong>${tierCount} cases for ${priceStr}</strong>`;
          }
          if (count >= tierCount) {
            // Applied — show the savings for that specific case
            const base = this.pickPrice(this.state.bundle[tierIdx]);
            const tierSaved = base - this.discountedPrice(tierIdx, base);
            return `<span class="bb-reward-hit">✓ ${pctValue}% off case ${tierCount} (save ${this.fmt(tierSaved)})</span>`;
          }
          const remaining = tierCount - count;
          if (count === 0) {
            return `<strong>${pctValue}% off your ${ordinal} case</strong>`;
          }
          return `Add ${remaining} more for <strong>${pctValue}% off the ${ordinal}</strong>`;
        };

        const callouts = [];
        if (has2) callouts.push(calloutFor(1, d2));
        if (has3) callouts.push(calloutFor(2, d3));
        this.rewardsEl.innerHTML = callouts.join(` ${sep} `);
      }

      // CTA — kept clickable so we can show a friendly size-not-picked error
      this.ctaEl.disabled = false;
      if (count === 0) {
        this.ctaEl.textContent = this.ctaEmptyLabel;
        this.ctaEl.dataset.state = "empty";
      } else if (count < this.bundleSize) {
        this.ctaEl.textContent = this.interpolate(this.ctaPartialTpl, { count, total: totalStr });
        this.ctaEl.dataset.state = "partial";
      } else {
        this.ctaEl.textContent = this.interpolate(this.ctaFullTpl, { count, total: totalStr });
        this.ctaEl.dataset.state = "full";
      }
    }

    // ---- popover ----
    openPopover(anchor, product) {
      // Toggle: clicking the same anchor while open closes it
      if (!this.popoverEl.hidden && this.popoverAnchor === anchor) {
        this.hidePopover();
        return;
      }

      // Move popover to <body> the first time so positioning is page-relative
      // and unaffected by any ancestor overflow:hidden / transform contexts.
      if (!this.popoverInBody) {
        document.body.appendChild(this.popoverEl);
        this.popoverInBody = true;
      }

      this.popoverAnchor = anchor;
      this.popTitleEl.textContent = `Pick a size · ${product.title}`;

      this.popOptsEl.innerHTML = "";
      product.variants.forEach(v => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "bb-popover-option";
        btn.disabled = !v.available;
        // Use option1 (the size value only) — matches the slot-1 dropdown
        // and avoids surfacing color/material on the option labels.
        const fullTitle = (v.title && v.title !== "Default Title") ? v.title : product.title;
        const title = v.option1 || fullTitle;
        btn.innerHTML =
          `<span class="bb-popover-option-title">${this.escape(title)}</span>` +
          `<span class="bb-popover-option-price">${this.fmt(v.price)}</span>`;
        if (v.available) {
          btn.addEventListener("click", (e) => {
            e.stopPropagation();
            this.addToBundle(product.productId, v.variantId);
            this.hidePopover();
          });
        }
        this.popOptsEl.appendChild(btn);
      });

      // Visual marker on the anchor card
      this.root.querySelectorAll(".bb-card[data-anchor]").forEach(el => el.removeAttribute("data-anchor"));
      anchor.dataset.anchor = "true";

      this.popoverEl.hidden = false;
      this.positionPopover();
    }

    positionPopover() {
      if (!this.popoverAnchor) return;
      const rect = this.popoverAnchor.getBoundingClientRect();
      const popW = this.popoverEl.offsetWidth;
      const popH = this.popoverEl.offsetHeight;
      const margin = 8;

      // page coordinates so popover stays glued to the card on scroll
      let left = rect.left + window.scrollX + rect.width / 2 - popW / 2;
      left = Math.max(
        window.scrollX + margin,
        Math.min(left, window.scrollX + window.innerWidth - popW - margin)
      );

      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove = spaceBelow < popH + 16 && rect.top > popH + 16;
      const top = placeAbove
        ? rect.top + window.scrollY - popH - 10
        : rect.bottom + window.scrollY + 10;

      this.popoverEl.style.left = left + "px";
      this.popoverEl.style.top = top + "px";
      this.popoverEl.dataset.side = placeAbove ? "above" : "below";

      const arrowX = rect.left + window.scrollX + rect.width / 2 - left;
      const arrowEl = this.popoverEl.querySelector(".bb-popover-arrow");
      if (arrowEl) arrowEl.style.left = arrowX + "px";
    }

    hidePopover() {
      this.popoverEl.hidden = true;
      this.popoverAnchor = null;
      this.root.querySelectorAll(".bb-card[data-anchor]").forEach(el => el.removeAttribute("data-anchor"));
    }

    bindPopoverDismiss() {
      document.addEventListener("click", (e) => {
        if (this.popoverEl.hidden) return;
        if (this.popoverEl.contains(e.target)) return;
        if (this.popoverAnchor && this.popoverAnchor.contains(e.target)) return;
        this.hidePopover();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.hidePopover();
      });
      window.addEventListener("resize", () => {
        if (!this.popoverEl.hidden) this.positionPopover();
      });
      // Note: deliberately NO scroll-close listener. With page-coordinate
      // positioning the popover scrolls with the anchor naturally — closing
      // on scroll made the popover feel "disappearing" on touch devices.
    }

    // ---- actions ----
    // Replaces (or sets) the locked first-slot variant. Slot 0 is reserved for
    // the current page's product when one exists; this just updates its variantId.
    setLockedVariant(variantId) {
      if (!this.currentProduct) return;
      const pick = { productId: this.currentProduct.productId, variantId };
      if (this.state.bundle.length === 0) {
        this.state.bundle.push(pick);
      } else {
        this.state.bundle[0] = pick;
      }
      this.clearSizeError();
      this.saveToStorage();
      this.render();
    }

    addToBundle(productId, variantId) {
      if (this.full()) return;
      this.clearSizeError();
      this.state.bundle.push({ productId, variantId });
      this.expanded = false; // next active step starts collapsed behind "+"
      this.saveToStorage();
      this.render();
    }

    changeStep(stepIndex) {
      this.state.bundle = this.state.bundle.slice(0, stepIndex);
      this.expanded = false;
      this.saveToStorage();
      this.render();
    }

    // ---- inline error handling ----
    showSizeError() {
      const select = this.root.querySelector(".bb-locked-select");
      const hint   = this.root.querySelector(".bb-locked-hint");
      if (select) {
        select.classList.remove("bb-locked-select--error");
        // force reflow so the shake animation restarts on repeated clicks
        void select.offsetWidth;
        select.classList.add("bb-locked-select--error");
        select.focus({ preventScroll: true });
        select.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (hint) {
        hint.textContent = "Please select a size";
        hint.classList.add("bb-locked-hint--error");
      }
    }
    clearSizeError() {
      const select = this.root.querySelector(".bb-locked-select");
      const hint   = this.root.querySelector(".bb-locked-hint");
      if (select) select.classList.remove("bb-locked-select--error");
      if (hint) {
        hint.classList.remove("bb-locked-hint--error");
        hint.textContent = "Select a size";
      }
    }

    // ---- localStorage persistence ----
    storageKey() { return "kaelhaus_bundle_state_v1"; }
    saveToStorage() {
      try {
        localStorage.setItem(this.storageKey(), JSON.stringify({
          bundle: this.state.bundle,
          productHandle: this.productHandle || null,
          savedAt: Date.now()
        }));
      } catch (_) { /* quota / unavailable — silently ignore */ }
    }
    restoreFromStorage() {
      try {
        const raw = localStorage.getItem(this.storageKey());
        if (!raw) return;
        const data = JSON.parse(raw);
        // Expire after 24h
        if (!data.savedAt || Date.now() - data.savedAt > 24 * 60 * 60 * 1000) return;
        // Only restore if we're back on the same product page
        if (this.productHandle && data.productHandle !== this.productHandle) return;
        // Validate every pick still resolves to a real variant
        const valid = (data.bundle || []).filter(pk => {
          const p = this.productById(pk.productId);
          return p && p.variants.some(v => v.variantId === pk.variantId && v.available);
        });
        if (valid.length > 0) this.state.bundle = valid;
      } catch (_) { /* corrupt JSON — ignore */ }
    }
    clearStorage() {
      try { localStorage.removeItem(this.storageKey()); } catch (_) {}
    }

    // ---- Analytics pixels (Meta / Pinterest / GA4 / TikTok) ----
    fireAtcPixels() {
      // Derive line-level data from the current bundle state
      const currencyCode = this.moneyFormatter.resolvedOptions().currency || "USD";
      const lines = this.state.bundle.map((pk, i) => {
        const p = this.productById(pk.productId) || {};
        const v = this.variantOfPick(pk) || {};
        const priceCents = this.discountedPrice(i, v.price || 0);
        return {
          productId:  p.productId,
          variantId:  pk.variantId,
          handle:     p.handle,
          title:      p.title || "",
          price:      priceCents / 100,
          quantity:   1
        };
      });
      const totalValue = lines.reduce((s, l) => s + l.price * l.quantity, 0);
      const numItems   = lines.length;

      // ---- Meta Pixel ----
      if (this.fireMeta && typeof window.fbq === "function") {
        try {
          window.fbq("track", "AddToCart", {
            content_ids:  lines.map(l => String(l.variantId)),
            content_type: "product",
            contents:     lines.map(l => ({
              id:         String(l.variantId),
              quantity:   l.quantity,
              item_price: l.price
            })),
            currency:  currencyCode,
            value:     totalValue,
            num_items: numItems
          });
        } catch (e) { console.warn("[bundle-builder] fbq failed", e); }
      }

      // ---- Pinterest tag ----
      if (this.firePinterest && typeof window.pintrk === "function") {
        try {
          window.pintrk("track", "addtocart", {
            value:          totalValue,
            order_quantity: numItems,
            currency:       currencyCode,
            line_items:     lines.map(l => ({
              product_id:         String(l.productId),
              product_variant_id: String(l.variantId),
              product_name:       l.title,
              product_price:      l.price,
              product_quantity:   l.quantity
            }))
          });
        } catch (e) { console.warn("[bundle-builder] pintrk failed", e); }
      }

      // ---- Google Analytics 4 (gtag) ----
      if (this.fireGa4 && typeof window.gtag === "function") {
        try {
          window.gtag("event", "add_to_cart", {
            currency: currencyCode,
            value:    totalValue,
            items:    lines.map(l => ({
              item_id:   String(l.variantId),
              item_name: l.title,
              price:     l.price,
              quantity:  l.quantity
            }))
          });
        } catch (e) { console.warn("[bundle-builder] gtag failed", e); }
      }

      // ---- TikTok pixel ----
      if (this.fireTiktok && window.ttq && typeof window.ttq.track === "function") {
        try {
          window.ttq.track("AddToCart", {
            contents: lines.map(l => ({
              content_id:   String(l.variantId),
              content_type: "product",
              content_name: l.title,
              price:        l.price,
              quantity:     l.quantity
            })),
            value:    totalValue,
            currency: currencyCode
          });
        } catch (e) { console.warn("[bundle-builder] ttq failed", e); }
      }
    }

    // ---- Aggressive cart UI refresh ----
    async refreshCartUI() {
      // 1. Fetch canonical cart state — /cart/add.js only returns the added
      // line item, /cart.js returns the whole cart (item_count, items, totals).
      let cart = null;
      try {
        const res = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
        if (res.ok) cart = await res.json();
      } catch (_) { /* network blip — best-effort */ }

      // 2. Update common cart-count indicators directly (covers themes whose
      // bubble doesn't auto-update from events).
      if (cart && typeof cart.item_count === "number") {
        this.updateCartCount(cart.item_count);
      }

      // 3. Fire a wide net of events on BOTH document and window so any
      // theme listener picks up the change.
      const eventNames = [
        "cart:refresh", "cart:updated", "cart:added",
        "cart-update", "cart:items-changed", "cart:change"
      ];
      eventNames.forEach(name => {
        const ev = new CustomEvent(name, {
          bubbles: true,
          detail: { source: "bundle-builder", cart }
        });
        try { document.dispatchEvent(ev); } catch (_) {}
        try { window.dispatchEvent(ev); }   catch (_) {}
      });

      // 4. Horizon-style pub/sub if it's exposed globally
      if (typeof window.publish === "function") {
        eventNames.forEach(name => {
          try { window.publish(name, { cart }); } catch (_) {}
        });
      }

      // 5. Call any imperative refresh methods on the cart drawer custom
      // element — most Horizon-style drawers re-render from their own state,
      // not from DOM swaps, so this is what actually populates them.
      if (this.cartDrawerSelector) {
        try {
          document.querySelectorAll(this.cartDrawerSelector).forEach(el => {
            ["refresh", "reload", "update", "fetchCart", "render", "renderContents"]
              .forEach(method => {
                if (typeof el[method] === "function") {
                  try { el[method](cart); } catch (_) {}
                }
              });
          });
        } catch (_) {}
      }
    }

    // Manually nudge common cart-count nodes in the page.
    updateCartCount(count) {
      const selectors = [
        "[data-cart-count]",
        "[data-header-cart-count]",
        ".cart-count",
        ".cart-count-bubble",
        ".cart-count-number",
        ".icon-cart-count",
        ".js-cart-count",
        ".header__cart-count",
        "cart-icon-bubble"
      ];
      selectors.forEach(sel => {
        try {
          document.querySelectorAll(sel).forEach(el => {
            // Prefer a dedicated text child if there is one
            const inner = el.querySelector(
              ".count, .cart-count-bubble__count, .cart-count-number, span"
            );
            const target = (inner && !inner.children.length) ? inner : el;
            target.textContent = String(count);
            if (count > 0) {
              el.removeAttribute("hidden");
              el.classList.remove("hidden", "empty", "cart-count--empty");
            }
          });
        } catch (_) {}
      });
    }

    // ---- Cart drawer + success confirmation ----
    // Walk the configured selector, calling .open()/.show() on any matching
    // custom element. Falls back to .click() if the element doesn't expose
    // those methods.
    openCartDrawer() {
      if (!this.cartDrawerSelector) return;
      let els;
      try { els = document.querySelectorAll(this.cartDrawerSelector); }
      catch (_) { return; }
      for (const el of els) {
        try {
          if (typeof el.open === "function")    { el.open();    return; }
          if (typeof el.show === "function")    { el.show();    return; }
          if (typeof el.refresh === "function") { el.refresh(); }
          if (el.tagName === "BUTTON" || el.tagName === "A") {
            el.click();
            return;
          }
        } catch (_) { /* try next */ }
      }
    }

    // Briefly flips the CTA to a green "Added to cart" state so the customer
    // gets explicit confirmation even if the cart drawer doesn't visibly update.
    showAddedConfirmation() {
      if (!this.ctaEl) return;
      this.ctaEl.dataset.state = "added";
      this.ctaEl.textContent = "✓ Added to cart";
      this.ctaEl.disabled = true;
      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(() => {
        this.ctaEl.removeAttribute("data-state");
        this.render();
      }, 2200);
    }

    // ---- Section Rendering API helpers ----
    // Hard-replace known cart custom elements with fresh copies parsed out of
    // the section HTML. Babric's <cart-drawer-component> only exposes open/close
    // and renders its contents from DOM, so re-mounting the element with new
    // markup is what actually updates what the customer sees on next open.
    replaceCartCustomElements(html) {
      if (!html) return;
      try {
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        const tags = [
          "cart-drawer-component",
          "cart-drawer",
          "cart-icon-bubble",
          "cart-notification"
        ];
        tags.forEach(tag => {
          const fresh = tmp.querySelector(tag);
          if (!fresh) return;
          const existing = document.querySelector(tag);
          if (!existing || existing === fresh) return;
          // Preserve currently-open state across the swap
          const wasOpen =
            existing.hasAttribute("open") ||
            existing.classList.contains("open") ||
            existing.getAttribute("data-state") === "open";
          const replacement = fresh.cloneNode(true);
          existing.replaceWith(replacement);
          if (wasOpen) {
            try {
              if (typeof replacement.open === "function") replacement.open();
              else replacement.setAttribute("open", "");
            } catch (_) {}
          }
        });
      } catch (_) {}
    }

    // Replace the contents of #shopify-section-<id> with the freshly-rendered
    // HTML returned by /cart/add.js when called with `sections=...`.
    updateSection(id, html) {
      if (!id || !html) return;
      const target = document.getElementById(`shopify-section-${id}`);
      if (!target) return;
      // Shopify returns the full <div id="shopify-section-id">…</div> wrapper.
      // Unwrap so we replace only the inner content (preserves the target node).
      try {
        const tmp = document.createElement("div");
        tmp.innerHTML = html.trim();
        const wrapped = tmp.querySelector(`#shopify-section-${id}`);
        target.innerHTML = wrapped ? wrapped.innerHTML : html;
      } catch (_) {
        target.innerHTML = html;
      }
    }

    // ---- URL ?bundle_case=<handle> pre-selects an additional product ----
    applyUrlPreselect() {
      try {
        const url = new URL(window.location.href);
        const handle = url.searchParams.get("bundle_case");
        if (!handle) return;
        const p = this.products.find(x => x.handle === handle);
        if (!p) return;
        // Don't re-add if already in bundle
        if (this.state.bundle.some(pk => pk.productId === p.productId)) return;
        if (this.full()) return;
        const v = p.variants.find(v => v.available);
        if (v) this.state.bundle.push({ productId: p.productId, variantId: v.variantId });
      } catch (_) {}
    }

    async submit() {
      const count = this.count();
      if (count === 0) {
        // Inline error: scroll to + highlight the size dropdown
        this.showSizeError();
        return;
      }

      const bundleId =
        "bundle_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

      const items = this.state.bundle.map((pk, i) => ({
        id: pk.variantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: `${i + 1} of ${count}`
        }
      }));

      this.ctaEl.setAttribute("aria-busy", "true");
      this.ctaEl.disabled = true;

      try {
        const body = { items };
        // Ask Shopify to re-render cart sections so we can refresh them in place
        if (this.refreshSections.length > 0) {
          body.sections = this.refreshSections.join(",");
          body.sections_url = window.location.pathname + window.location.search;
        }

        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ description: "Could not add to cart" }));
          throw new Error(err.description || "Could not add to cart");
        }

        const data = await res.json();

        // Swap freshly-rendered cart sections into the DOM
        if (data.sections) {
          Object.keys(data.sections).forEach(id => {
            const html = data.sections[id];
            this.updateSection(id, html);
            // Some themes render the drawer / bubble as custom elements
            // OUTSIDE the wrapping shopify-section div (e.g. in theme.liquid).
            // Look for known cart custom elements in the returned HTML and
            // replace the matching ones in the live DOM directly.
            this.replaceCartCustomElements(html);
          });
        }

        // Fire analytics events (Meta, Pinterest, GA4, TikTok) — done BEFORE
        // the cart UI refresh so nothing races the pixels.
        this.fireAtcPixels();

        // Refresh the cart bubble + drawer state silently. We deliberately
        // do NOT open the drawer — the customer should stay on the product
        // page and open it themselves via the cart icon if they want to.
        await this.refreshCartUI();

        // Clear persisted state on successful add
        this.clearStorage();

        if (this.cartRedirect) {
          window.location.href = "/cart";
        } else if (this.reloadOnAdd) {
          // Hard fallback for themes whose cart UI doesn't refresh otherwise.
          window.location.reload();
        } else {
          this.state.bundle = [];
          this.showAddedConfirmation();
          this.render();
        }
      } catch (e) {
        console.error("[bundle-builder] add to cart failed", e);
        alert(e.message || "Sorry, something went wrong adding the bundle to cart.");
        this.render();
      } finally {
        this.ctaEl.removeAttribute("aria-busy");
      }
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-bundle-builder]").forEach(root => {
      if (root.__bundleBuilder) return;
      root.__bundleBuilder = new BundleBuilder(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();
